import { useCallback, useEffect, useRef, useState } from "react"
import { useDialogFocus } from "./useDialogFocus"
import { getDraft, history, restore } from "./api"
import type { SectionVersion } from "./contract"
import { useSimStore } from "./store"

interface Props {
  threadId: string
  sectionId: string
  label: string
  onClose: () => void
}

const when = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString()
}

/** Version history for one section. Restore re-saves the whole draft as it stood at that version. */
export function HistoryDrawer({ threadId, sectionId, label, onClose }: Props) {
  const [rows, setRows] = useState<SectionVersion[] | null>(null)
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)
  const closeBtn = useRef<HTMLButtonElement>(null)
  useDialogFocus(onClose, closeBtn)

  const load = useCallback(() => {
    setError("")
    history(threadId, sectionId).then((r) => setRows([...r].reverse())).catch(() => setError("Couldn't load the history."))
  }, [threadId, sectionId])
  useEffect(load, [load])

  const doRestore = async (version: number) => {
    setBusy(true)
    setError("")
    try {
      const base = useSimStore.getState().threads[threadId]?.draft?.version ?? 0
      await restore(threadId, version, base)
      useSimStore.getState().setDraft(threadId, await getDraft(threadId))
      onClose()
    } catch (err) {
      setError((err as { status?: number })?.status === 409
        ? "The draft changed since you opened this. Close the history and try again."
        : "Couldn't restore that version.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <aside className="osia-drawer" role="dialog" aria-label={`History: ${label}`}>
      <div className="osia-drawer-head">
        <div><p className="ov">VERSION HISTORY</p><h3>{label}</h3></div>
        <button ref={closeBtn} type="button" className="ib" aria-label="Close history" onClick={onClose}>×</button>
      </div>
      {error ? <p className="note" role="alert">{error}</p> : null}
      {rows === null && !error ? <p className="note">Loading…</p> : null}
      {rows?.length === 0 ? <p className="note">No saved versions yet.</p> : null}
      <ul className="osia-versions">
        {rows?.map((v) => (
          <li key={v.version} className="osia-version">
            <div className="osia-version-meta">
              <span className="mono">v{v.version} · {v.author}</span>
              <span className="note">{when(v.created_at)}{v.note ? ` · ${v.note}` : ""}</span>
            </div>
            <p className="osia-snippet">{v.markdown.slice(0, 160)}{v.markdown.length > 160 ? "…" : ""}</p>
            <button type="button" className="sbtn" disabled={busy} onClick={() => void doRestore(v.version)}>
              Restore draft to v{v.version}
            </button>
          </li>
        ))}
      </ul>
      <p className="note">Restoring brings every section back to how it was at that version, as a new version.</p>
    </aside>
  )
}
